import {useEffect, useMemo, useState} from 'react';
import {Close, ContentCopy, Edit, People, PowerSettingsNew} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Divider,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography
} from '@mui/material';
import {Link} from 'react-router-dom';
import {PRIVATE_ADMIN_USERS} from 'config/router/paths';
import {
  downloadCsv,
  filterLinks,
  formatDate,
  isExpired,
  isSoon,
  linkName,
  providerHost
} from 'utils/adminManagement';
import {M3UEditor, ToggleDialog, UserEditor} from './management/ManagementEditors';
import {
  Capacity,
  EmptyResults,
  Expiry,
  FilterSelect,
  LinkStatus,
  ManagementHeader,
  ManagementMessages,
  SearchField,
  SummaryCards
} from './management/ManagementUI';
import {useManagement, useManagementFilters} from './management/useManagement';

const DEFAULTS = {q: '', expiry: 'current', state: 'all', capacity: 'all', sort: 'expiry'};

export default function M3UManager() {
  const model = useManagement();
  const [filters, update, reset] = useManagementFilters(DEFAULTS);
  const [editor, setEditor] = useState(null);
  const [toggle, setToggle] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const [newUserLink, setNewUserLink] = useState(null);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const filterKey = JSON.stringify(filters);
  const rows = filterLinks(model.m3us, filters);
  useEffect(() => {
    setPage(0);
  }, [filterKey]);
  const currentPage = Math.min(page, Math.max(0, Math.ceil(rows.length / pageSize) - 1));
  const detail = model.m3us.find((item) => item.id === detailId);
  const stats = useMemo(() => {
    const current = model.m3us.filter((item) => !isExpired(item.expiresAt));
    const active = current.filter((item) => item.enabled);
    return [
      {
        label: 'Enlaces vigentes',
        value: current.length,
        hint: `${model.m3us.length} enlaces en total`
      },
      {
        label: 'Caducan en 30 días',
        value: current.filter((item) => isSoon(item.expiresAt)).length,
        hint: 'Prepara las próximas renovaciones',
        color: 'warning'
      },
      {
        label: 'Pantallas asignadas',
        value: `${active.reduce((sum, m) => sum + m.assignedScreens, 0)} / ${active.reduce(
          (sum, m) => sum + m.maxClients,
          0
        )}`,
        hint: 'En enlaces activados y vigentes'
      },
      {
        label: 'Exceso de capacidad',
        value: model.m3us.filter((m) => m.assignedScreens > m.maxClients).length,
        hint: 'Enlaces con más pantallas asignadas',
        color: 'error'
      }
    ];
  }, [model.m3us]);
  const copied = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      model.setFeedback({severity: 'success', message: 'URL del M3U copiada.'});
    } catch {
      model.setFeedback({
        severity: 'error',
        message: 'No se ha podido copiar. Puedes seleccionar la URL en la edición del enlace.'
      });
    }
  };
  const exportRows = () =>
    downloadCsv('enlaces-m3u.csv', [
      [
        'ID',
        'Nombre',
        'Proveedor',
        'Activado',
        'Caducidad',
        'Pantallas asignadas',
        'Pantallas permitidas',
        'Usuarios',
        'Último uso',
        'Notas'
      ],
      ...rows.map((m) => [
        m.id,
        linkName(m),
        providerHost(m.path),
        m.enabled ? 'Sí' : 'No',
        m.expiresAt,
        m.assignedScreens,
        m.maxClients,
        m.userCount,
        m.lastUseAt,
        m.notes
      ])
    ]);
  return (
    <Box sx={{maxWidth: 1600, mx: 'auto'}}>
      <ManagementHeader
        section="m3us"
        title="Enlaces M3U"
        description="Controla las renovaciones, la capacidad contratada y los usuarios de cada enlace."
        model={model}
        onAdd={() => setEditor({})}
        onExport={exportRows}
      />
      <SummaryCards items={stats} />
      <Paper variant="outlined" sx={{p: 2, mb: 2}}>
        <Stack direction="row" gap={1.5} flexWrap="wrap">
          <SearchField
            label="Buscar enlace, proveedor o notas"
            value={filters.q}
            onChange={(q) => update({q})}
          />
          <FilterSelect
            label="Caducidad"
            value={filters.expiry}
            onChange={(expiry) => update({expiry})}
            options={[
              ['current', 'No caducados'],
              ['soon', 'Próximos 30 días'],
              ['expired', 'Caducados'],
              ['all', 'Todos']
            ]}
          />
          <FilterSelect
            label="Estado"
            value={filters.state}
            onChange={(state) => update({state})}
            options={[
              ['all', 'Todos'],
              ['enabled', 'Activados'],
              ['disabled', 'Desactivados']
            ]}
          />
          <FilterSelect
            label="Capacidad"
            value={filters.capacity}
            onChange={(capacity) => update({capacity})}
            options={[
              ['all', 'Cualquiera'],
              ['available', 'Con pantallas libres'],
              ['full', 'Completos o excedidos'],
              ['over', 'Con exceso']
            ]}
          />
          <FilterSelect
            label="Ordenar por"
            value={filters.sort}
            onChange={(sort) => update({sort})}
            options={[
              ['expiry', 'Caducidad más próxima'],
              ['name', 'Nombre'],
              ['capacity', 'Más capacidad libre'],
              ['assigned', 'Más pantallas asignadas']
            ]}
          />
        </Stack>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{mt: 1}}>
          <Typography variant="caption" color="text.secondary">
            {rows.length} de {model.m3us.length} enlaces · Las pantallas asignadas son la suma de
            los máximos de los usuarios.
          </Typography>
          <Button size="small" onClick={reset}>
            Restablecer
          </Button>
        </Stack>
      </Paper>
      <ManagementMessages model={model} />
      <Paper variant="outlined">
        <TableContainer>
          <Table aria-label="Gestor de enlaces M3U" sx={{minWidth: 920}}>
            <TableHead>
              <TableRow>
                <TableCell>Enlace / proveedor</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Caducidad</TableCell>
                <TableCell>Capacidad asignada</TableCell>
                <TableCell align="center">Usuarios</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.slice(currentPage * pageSize, (currentPage + 1) * pageSize).map((m) => (
                <TableRow key={m.id} hover>
                  <TableCell>
                    <Button
                      sx={{
                        p: 0,
                        justifyContent: 'flex-start',
                        textAlign: 'left',
                        textTransform: 'none',
                        fontWeight: 700
                      }}
                      onClick={() => setDetailId(m.id)}
                    >
                      {linkName(m)}
                    </Button>
                    <Typography variant="caption" display="block" color="text.secondary">
                      #{m.id} · {providerHost(m.path)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <LinkStatus link={m} />
                  </TableCell>
                  <TableCell>
                    <Expiry value={m.expiresAt} />
                  </TableCell>
                  <TableCell>
                    <Capacity assigned={m.assignedScreens} max={m.maxClients} />
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      startIcon={<People />}
                      onClick={() => setDetailId(m.id)}
                      aria-label={`Ver ${m.userCount} usuarios de ${linkName(m)}`}
                    >
                      {m.userCount}
                    </Button>
                  </TableCell>
                  <TableCell align="right" sx={{whiteSpace: 'nowrap'}}>
                    <Tooltip title="Editar o renovar">
                      <IconButton onClick={() => setEditor(m)} aria-label={`Editar ${linkName(m)}`}>
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Copiar URL">
                      <IconButton
                        onClick={() => copied(m.path)}
                        aria-label={`Copiar URL de ${linkName(m)}`}
                      >
                        <ContentCopy fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={m.enabled ? 'Desactivar' : 'Activar'}>
                      <IconButton
                        color={m.enabled ? 'default' : 'success'}
                        onClick={() => setToggle(m)}
                        aria-label={`${m.enabled ? 'Desactivar' : 'Activar'} ${linkName(m)}`}
                      >
                        <PowerSettingsNew fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        {!rows.length && (
          <EmptyResults loading={model.loading} onReset={reset}>
            No hay enlaces que coincidan con los filtros.
          </EmptyResults>
        )}
        <TablePagination
          component="div"
          count={rows.length}
          page={currentPage}
          rowsPerPage={pageSize}
          onPageChange={(_, next) => setPage(next)}
          onRowsPerPageChange={(e) => {
            setPageSize(Number(e.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Enlaces por página"
          labelDisplayedRows={({from, to, count}) => `${from}–${to} de ${count}`}
          getItemAriaLabel={(type) => (type === 'next' ? 'Página siguiente' : 'Página anterior')}
        />
      </Paper>
      {model.generatedAt && (
        <Typography variant="caption" color="text.secondary" display="block" sx={{mt: 1}}>
          Actualizado: {formatDate(model.generatedAt)}. El CSV exporta los resultados filtrados.
        </Typography>
      )}
      <Drawer
        anchor="right"
        open={Boolean(detail)}
        onClose={() => setDetailId(null)}
        PaperProps={{sx: {width: {xs: '100%', sm: 480}}}}
      >
        {detail && (
          <Stack spacing={2.5} sx={{p: 3}}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h5" fontWeight={700}>
                {linkName(detail)}
              </Typography>
              <IconButton onClick={() => setDetailId(null)} aria-label="Cerrar detalle">
                <Close />
              </IconButton>
            </Stack>
            <Typography color="text.secondary">
              #{detail.id} · {providerHost(detail.path)}
            </Typography>
            <LinkStatus link={detail} />
            <Capacity assigned={detail.assignedScreens} max={detail.maxClients} />
            <Box>
              <Typography variant="overline">Caducidad</Typography>
              <Expiry value={detail.expiresAt} />
            </Box>
            <Box>
              <Typography variant="body2" color="text.secondary">
                Creado: {formatDate(detail.createdAt)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Último uso:{' '}
                {detail.lastUseAt ? formatDate(detail.lastUseAt) : 'Todavía no utilizado'}
              </Typography>
            </Box>
            {detail.notes && (
              <Typography sx={{whiteSpace: 'pre-wrap', overflowWrap: 'anywhere'}}>
                {detail.notes}
              </Typography>
            )}
            <Button variant="outlined" startIcon={<Edit />} onClick={() => setEditor(detail)}>
              Editar o renovar enlace
            </Button>
            <Divider />
            <Typography variant="h6">Usuarios asociados ({detail.userCount})</Typography>
            {model.users
              .filter((user) => user.m3uId === detail.id)
              .map((user) => (
                <Paper key={user.id} variant="outlined" sx={{p: 1.5}}>
                  <Stack direction="row" justifyContent="space-between" gap={1}>
                    <Box sx={{minWidth: 0}}>
                      <Typography fontWeight={600} sx={{overflowWrap: 'anywhere'}}>
                        {user.name || user.email}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {user.email}
                      </Typography>
                    </Box>
                    <Chip label={`${user.maxActiveStreams} pantallas`} size="small" />
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {user.enabled ? 'Activado' : 'Desactivado'} ·{' '}
                    {user.m3uExpiresAt ? `Hasta ${formatDate(user.m3uExpiresAt)}` : 'Sin caducidad'}
                  </Typography>
                </Paper>
              ))}
            {!detail.userCount && (
              <Typography color="text.secondary">
                Este enlace todavía no tiene usuarios asociados.
              </Typography>
            )}
            <Button
              component={Link}
              to={`${PRIVATE_ADMIN_USERS}?m3u=${detail.id}`}
              variant="contained"
              startIcon={<People />}
            >
              Gestionar estos usuarios
            </Button>
            <Button
              disabled={!detail.enabled || isExpired(detail.expiresAt)}
              onClick={() => setNewUserLink(detail.id)}
            >
              Crear usuario con este M3U
            </Button>
          </Stack>
        )}
      </Drawer>
      {editor && (
        <M3UEditor
          key={editor.id || 'new'}
          item={editor.id ? editor : null}
          onClose={() => setEditor(null)}
          onSaved={(result) => {
            setEditor(null);
            model.saved(result, 'Enlace M3U guardado.');
          }}
        />
      )}
      {toggle && (
        <ToggleDialog
          kind="m3us"
          item={toggle}
          onClose={() => setToggle(null)}
          onSaved={(result) => {
            setToggle(null);
            model.saved(result, 'Estado del enlace actualizado.');
          }}
        />
      )}
      {newUserLink && (
        <UserEditor
          links={model.m3us}
          initialM3uId={newUserLink}
          onClose={() => setNewUserLink(null)}
          onSaved={(result) => {
            setNewUserLink(null);
            model.saved(result, 'Usuario creado y asociado al M3U.');
          }}
        />
      )}
    </Box>
  );
}
